import React from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError, AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import {
	PendingReleaseEventRequest,
	PendingReleaseEventResponse,
} from '@data/blockReleases/types/PendingReleaseEvent.types';
import { createPendingReleaseEvent as createPendingReleaseEventAction } from '@data/blockReleases/actionCreators/BlockReleaseActionCreators';
import { ENDPOINT_BLOCK_RELEASES_SLOTS } from '@utilities/apiConstants';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import { useToast } from '@utilities/hooks/useToast/useToast';
import { ToastType } from '@interfaces/ToastStatus/ToastStatusState';
import { ToastConstants } from '@utilities/toastConstants';
import { shouldDisableErrorHandlerByCode } from '@utilities/shouldDisableErrorHandlerByCode/shouldDisableErrorHandlerByCode';
import { BLOCK_RELEASE_REQUEST_CONFIRM_MODAL } from '@components/Modal/modalConstants';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { useBlockReleaseRequestContext } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { useGetBlockReleasesByNpi } from '@data/blockReleases/hooks/useGetBlockReleasesByNpi';

const errorCodeToMessage: Record<string, string> = {
	CS03: 'Block already released',
	CS04: 'Release already requested',
};

export const useCreatePendingReleaseEvent = () => {
	const [pendingReleaseEvent, setPendingReleaseEvent] =
		React.useState<PendingReleaseEventResponse>();
	const [inlineError, setInlineError] = React.useState<string>();
	const dispatch = useDispatch();
	const history = useHistory();
	const displayToast = useToast();
	const { selectedReleaseInfo, setCombinedReleases, selectedSurgeonFilter } = useBlockReleaseRequestContext();
	const { closeModal } = useModalContext();
	const { selectedRelease: { state } } = selectedReleaseInfo;
	const { npis } = useSurgeons();
	const { getReleases } = useGetBlockReleasesByNpi();

	const createPendingReleaseEvent = React.useCallback(
		(slotId: string, body: PendingReleaseEventRequest) => {
			const url = `${ENDPOINT_BLOCK_RELEASES_SLOTS}/${slotId}`;

			const onSuccess = (response: AxiosResponse<PendingReleaseEventResponse>) => {
				setPendingReleaseEvent(response.data);
				displayToast(ToastType.SUCCESS, ToastConstants.BLOCK_RELEASE_REQUESTED);
				if (state === BlockReleaseState.RECOMMENDED_RELEASE || state === BlockReleaseState.FLIP_RECOMMENDATION) {
					const selectedSurgeonQueryParam = new URLSearchParams({ surgeon: selectedSurgeonFilter }).toString();
					closeModal(BLOCK_RELEASE_REQUEST_CONFIRM_MODAL.id + slotId);
					getReleases({
						query: 'RELEASE_BY_NPI',
						npis,
						types: [BlockReleaseState.RECOMMENDED_RELEASE, BlockReleaseState.PENDING_RELEASE],
					}, {
						onSuccess: (res) => {
							setCombinedReleases(res.data);
							history.replace(`${ROUTE_CONSTANTS.BLOCK_RELEASE_RECOMMENDATION_LIST}?${selectedSurgeonQueryParam}`);
						},
					});
				} else {
					history.push(ROUTE_CONSTANTS.SCHEDULING, { disableClearToast: true });
				}
			};


			const onFailure = (error: AxiosError<PendingReleaseEventResponse>) => {
				if (error.response?.status !== 422 || !['CS03', 'CS04'].includes(error.response.data.code)) return;
				setInlineError(errorCodeToMessage[error.response?.data.code]);
			};

			dispatch(createPendingReleaseEventAction(body, {
				onSuccess,
				onFailure,
				url,
				shouldDisableErrorHandler: shouldDisableErrorHandlerByCode(['CS03', 'CS04'])
			}));
		},
		[closeModal, dispatch, displayToast, getReleases, history, npis, selectedSurgeonFilter, setCombinedReleases, state],
	);

	return {
		pendingReleaseEvent,
		createPendingReleaseEvent,
		inlineError
	};
};
